import AccountSwitcher from "components/AccountSwitcher";
import { Nav } from "react-bootstrap";
import { Link, useLocation } from "react-router-dom";
import { FiMessageSquare } from "react-icons/fi";
import { get, includes, some, find } from "lodash";
import {
  IndividualContributionPending,
  IndividualInvestmentProfileUpdate,
  IndividualPortfolioPending,
  IndividualPortfolioUpdate,
  IndividualRequiredUpdatesPending,
  IndividualRolloverInfoPending,
  IndividualApplicationInfoComplete,
  IndividualDisclosuresComplete,
  IndividualInvestmentInfoComplete,
  IndividualTermsPending,
} from "statics/states";
import { activeAccountState } from "store/selectors/user";
import React from "react";
import type { ApolloClient } from "@apollo/client";
import { push } from "store/actions/router";
import { exitInvestmentProfileUpdate } from "actions/userActions";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { setAccountSession } from "actions/accountActions";
import type { RootState } from "types/state";
import type { IraAccount } from "types/models";

// No props are passed by a parent — every prop comes from redux (connect) or
// the withApollo HOC (client). Empty object type (no own props).
type IndividualNavLeftOwnProps = Record<never, never>;

const mapStateToProps = (state: RootState) => {
  // state.user.userState is typed `object`; the runtime value here is the
  // user-state string. Same boundary cast as IconLayout/SideBar.
  const userState = get(state.user, "userState.state", "") as string;
  // activeAccountState returns Maybe<string> | null (the account's state
  // string at runtime); cast at the boundary for the includes() check below.
  const accountState = activeAccountState(state) as string;
  const accounts = state.accounts.iraAccounts;
  const rolloverOnboardingStates = [
    IndividualRolloverInfoPending,
    IndividualApplicationInfoComplete,
    IndividualDisclosuresComplete,
    IndividualInvestmentInfoComplete,
    IndividualTermsPending,
  ];
  const userInFinalOnboarding = [
    IndividualPortfolioPending,
    IndividualContributionPending,
  ].includes(userState);
  const userUpdatingPortfolio = [
    IndividualInvestmentProfileUpdate,
    IndividualPortfolioUpdate,
  ].includes(userState);
  const userInRequiredUpdates = [IndividualRequiredUpdatesPending].includes(
    userState
  );

  const userInRolloverOnboarding = [
    IndividualRolloverInfoPending,
    IndividualApplicationInfoComplete,
    IndividualDisclosuresComplete,
    IndividualInvestmentInfoComplete,
    IndividualTermsPending,
  ].includes(accountState);

  const accountIsOnboarding = (account: IraAccount) =>
    includes(rolloverOnboardingStates, account.state);

  const activeRolloverAccount = some(accounts, accountIsOnboarding);

  const activeAccount = find(accounts, accountIsOnboarding);

  const activeAccountId = activeAccount ? activeAccount.id : null;

  return {
    accountState,
    activeAccountId,
    // same boundary cast as the userState local above (original `string` propType)
    userState: get(state.user, "userState.state", "") as string,
    activeRolloverAccount,
    userInFinalOnboarding,
    userUpdatingPortfolio,
    userInRequiredUpdates,
    userInRolloverOnboarding,
  };
};

const mapDispatchToProps = {
  push,
  exitInvestmentProfileUpdate,
  setAccountSession,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type IndividualNavLeftProps = PropsFromRedux & IndividualNavLeftOwnProps;

const IndividualNavLeft = ({
  userInRequiredUpdates,
  push,
  userInFinalOnboarding,
  userUpdatingPortfolio,
  userState,
  exitInvestmentProfileUpdate,
  userInRolloverOnboarding,
  activeRolloverAccount,
  activeAccountId,
  setAccountSession,
  client,
}: WithApolloClient<IndividualNavLeftProps>) => {
  const { pathname } = useLocation();
  const shouldHideSupportLink =
    userInFinalOnboarding ||
    userUpdatingPortfolio ||
    userInRequiredUpdates ||
    userInRolloverOnboarding;

  const shouldShowFinishRollover =
    activeRolloverAccount && includes(pathname, "/dashboard");

  let link;
  switch (userState) {
    case IndividualContributionPending:
      link = "/individual/setup_contribution";
      break;
    case IndividualPortfolioPending:
    case IndividualPortfolioUpdate:
      link = "/individual/portfolio_selection";
      break;
    case IndividualInvestmentProfileUpdate:
      link = "/individual/investment";
      break;
    default:
      link = "/dashboard";
  }

  return (
    <div className="individual-account-switcher">
      <AccountSwitcher />

      {!shouldHideSupportLink && (
        <Nav className="support-link">
          <Link
            to="/dashboard/support"
            className="support-link-target-box"
            onClick={() => push("/dashboard/support")}
          >
            <p className="support-link">
              Support <FiMessageSquare />
            </p>
          </Link>
        </Nav>
      )}

      {(userInFinalOnboarding || userUpdatingPortfolio) &&
        !includes(pathname, "/individual") && (
          <Nav className="support-link">
            <Link
              style={{ color: "white" }}
              className="support-link-target-box"
              to={link}
            >
              <p className="support-link">Finish Setup</p>
            </Link>
          </Nav>
        )}

      {shouldShowFinishRollover && (
        <Nav className="support-link">
          {/* Pre-existing quirk: this Link is used purely as an onClick button
              and intentionally omits the required `to` prop. Preserve behavior. */}
          {/* @ts-expect-error react-router LinkProps requires `to`; omitted here on purpose */}
          <Link
            style={{ color: "white" }}
            className="support-link-target-box"
            onClick={() => {
              // Link.onClick expects a void return; the handler was already
              // async (fire-and-forget) in JS, so void the floating promise.
              void (async () => {
                // client is optional on WithApolloClient; cast at the action boundary.
                // activeAccountId is string | null from mapStateToProps; preserve the
                // existing behavior (it is only invoked when an active rollover exists).
                await setAccountSession(
                  client as ApolloClient<object>,
                  activeAccountId as string
                );
                window.location.search = `?redirect_to=/dashboard`;
              })();
            }}
          >
            <p className="support-link">Resume Rollover Application</p>
          </Link>
        </Nav>
      )}
      {userState === IndividualInvestmentProfileUpdate &&
        !includes(pathname, "/dashboard") && (
          <Nav className="support-link">
            <Link
              to="/dashboard/profile/investment-profile"
              className="support-link-target-box"
              onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                // using push instead of the 'to' props because
                // exitInvestmentProfileUpdate needs to be called before route transition
                e.preventDefault();
                // client is optional on WithApolloClient; cast at the action boundary.
                // The original JS handler was async but never awaited this chain;
                // void it to preserve the same fire-and-forget behavior.
                void exitInvestmentProfileUpdate(
                  client as ApolloClient<object>
                ).then(() => push("/dashboard/profile/investment-profile"));
              }}
            >
              <p className="support-link">Exit Profile Update</p>
            </Link>
          </Nav>
        )}
    </div>
  );
};

export default connector(withApollo<IndividualNavLeftProps>(IndividualNavLeft));
